import type { PrV2CommentResponse, PrV2ReviewThread } from '@/lib/pr-v2/types';

import { removeMetadataFromComment } from '@/lib/ai/code-review-service/utils/metadata';
import { sanitizePromptContent } from '@/lib/security/prompt-sanitization';

export type PrV2ThreadAiChatSurface = 'changes' | 'discussion';

type BuildPrV2ThreadAiChatTextParams = {
  comments: PrV2CommentResponse[];
  thread?: PrV2ReviewThread;
  surface: PrV2ThreadAiChatSurface;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  return value as Record<string, unknown>;
}

function asPositiveInteger(value: unknown): number | null {
  if (typeof value === 'number' && Number.isInteger(value) && value > 0) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10);
    if (Number.isInteger(parsed) && parsed > 0) {
      return parsed;
    }
  }

  return null;
}

function resolveThreadRange(
  thread: PrV2ReviewThread | undefined,
  comments: PrV2CommentResponse[],
): { startLine: number; endLine: number } | null {
  if (thread) {
    const line = asPositiveInteger(thread.line) ?? asPositiveInteger(thread.originalLine);
    const startLine =
      asPositiveInteger(thread.startLine) ?? asPositiveInteger(thread.originalStartLine);
    if (line && startLine && line !== startLine) {
      return { startLine: Math.min(startLine, line), endLine: Math.max(startLine, line) };
    }
  }

  const root = comments[0];
  if (root) {
    const raw = asRecord(root.raw);
    const line = asPositiveInteger(raw?.line) ?? asPositiveInteger(root.line);
    const startLine = asPositiveInteger(raw?.start_line);
    if (line && startLine && line !== startLine) {
      return { startLine: Math.min(startLine, line), endLine: Math.max(startLine, line) };
    }
  }

  return null;
}

function getThreadLocation(
  comments: PrV2CommentResponse[],
  thread: PrV2ReviewThread | undefined,
  surface: PrV2ThreadAiChatSurface,
): string {
  const newestCommentWithPath = comments.findLast((comment) => comment.path);
  const rootComment = comments[0];
  const rootCommentRaw = rootComment ? asRecord(rootComment.raw) : null;
  const path =
    sanitizePromptContent(newestCommentWithPath?.path ?? thread?.path ?? '')?.replace(
      /\s+/g,
      ' ',
    ) || null;
  const line =
    asPositiveInteger(newestCommentWithPath?.line) ??
    asPositiveInteger(thread?.line) ??
    asPositiveInteger(thread?.originalLine) ??
    asPositiveInteger(rootComment?.line) ??
    asPositiveInteger(rootCommentRaw?.line) ??
    asPositiveInteger(rootCommentRaw?.original_line);

  if (path && typeof line === 'number') {
    const range = resolveThreadRange(thread, comments);
    if (range) {
      return `${path} · lines ${range.startLine}-${range.endLine}`;
    }

    return `${path} · line ${line}`;
  }

  if (path) {
    return path;
  }

  return surface === 'changes' ? 'Changes tab' : 'PR discussion';
}

function formatCommentBody(body: string | null): string {
  return sanitizePromptContent(removeMetadataFromComment(body ?? ''))?.trim() || '(no content)';
}

function formatCommentAuthor(comment: PrV2CommentResponse): string {
  return (
    sanitizePromptContent(comment.userLogin?.trim() || 'Unknown')?.replace(/\s+/g, ' ') || 'Unknown'
  );
}

export function buildPrV2ThreadAiChatText({
  comments,
  thread,
  surface,
}: BuildPrV2ThreadAiChatTextParams): string {
  const sourceLabel = surface === 'changes' ? 'Changes thread' : 'Discussion thread';
  const locationLabel = getThreadLocation(comments, thread, surface);
  const formattedComments = comments
    .map((comment, index) => {
      const prefix = `Comment ${index + 1} · ${formatCommentAuthor(comment)}`;
      return `${prefix}\n${formatCommentBody(comment.body)}`;
    })
    .join('\n\n');

  return `${sourceLabel}\nLocation: ${locationLabel}\n\n${formattedComments}`.trim();
}
